import template from './time_and_attendance_per_employee_report.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class ReportTimeAttendancePerEmployeeViewModel
{
	constructor(page)
	{
		this.page = page;

		let first_day = moment().format('YYYY-MM-01');
		let last_day = moment().format('YYYY-MM-') + moment().daysInMonth();

		this.date_start = ko.observable(new Date(first_day).toJSON().slice(0, 10));
		this.date_end = ko.observable(new Date(last_day).toJSON().slice(0, 10));

		this.attendance_data = ko.observableArray([]);
		this.summary_total_hours = ko.observable(0);
		this.summary_employees_count = ko.observable(0);
		this.table_visible = ko.observable(false);

		this.current_department_id = ko.observable();
		this.current_employee_id = ko.observable();

		this.selected_employee = ko.observable();
		this.selected_department = ko.observable();
		this.total_task_hours = ko.observable('0.00');
		this.total_leave_hours = ko.observable('0.00');
		this.total_holiday_hours = ko.observable('0.00');

		// In the constructor, replace the existing attendance_data.subscribe with this:
		this.attendance_data.subscribe((value) => {
			this.calculate_totals(value);
			// Show table only if there's data AND an employee is selected
			this.table_visible(value && value.length > 0 && this.selected_employee());
		});

		// Also update the selected_employee.subscribe to refresh table visibility:
		this.selected_employee.subscribe((newValue) => {
			if (newValue != null)
			{
				this.current_employee_id(newValue.employee_id);
				this.page.updateData();
			}
			else
			{
				this.current_employee_id(null);
				this.attendance_data([]); // Clear data when no employee selected
				this.page.updateData();
			}
		});

		
		this.date_start.subscribe((newValue) => {
			if (!newValue) return;

			// Clear any existing timeout
			if (this.start_date_timeout)
				clearTimeout(this.start_date_timeout);
			this.start_date_timeout = setTimeout(() => {
				let sd = moment(newValue, "YYYY-MM-DD", true);
				if (!sd.isValid()) return;

				// Only update end date to last day of start date month if end date is empty or not set
				// This allows for single-day queries when start and end are the same
				if (!this.date_end() || this.date_end() === '') {
					let month_end = moment(sd).endOf('month').format('YYYY-MM-DD');
					this.date_end(month_end);
				}

				this.page.updateData();
			}, 500); 
		});

		this.date_end.subscribe((newValue) => {
			if (!newValue) return;

			// Clear any existing timeout
			if (this.end_date_timeout)
				clearTimeout(this.end_date_timeout);

			// Set new timeout to wait for user to finish typing
			this.end_date_timeout = setTimeout(() => {
				let ed = moment(newValue, "YYYY-MM-DD", true);
				if (!ed.isValid()) return;

				if (newValue.length === 10)
				{
					let sd = moment(this.date_start(), "YYYY-MM-DD", true);
					if (sd.isValid() && ed.isBefore(sd))
					{
						Grape.alerts.alert({
							type: 'error',
							title: tr('Invalid Date Range'),
							message: tr('End date cannot be before start date')
						});
						return;
					}
					this.page.updateData();
				}
			}, 1000);
		});

	}

	calculate_totals(data)
	{
		const total_task_hours = data.reduce((sum, item) => {
			const hours = parseFloat(item.task_total_hours) || 0;
			return sum + hours;
		}, 0);

		// Calculate total leave hours
		const total_leave_hours = data.reduce((sum, item) => {
			const hours = parseFloat(item.leave_hours) || 0;
			return sum + hours;
		}, 0);

		// Calculate total holiday hours
		const total_holiday_hours = data.reduce((sum, item) => {
			const hours = parseFloat(item.holiday_hours) || 0;
			return sum + hours;
		}, 0);

		// Update the observables with formatted totals
		this.total_task_hours(total_task_hours.toFixed(2));
		this.total_leave_hours(total_leave_hours.toFixed(2));
		this.total_holiday_hours(total_holiday_hours.toFixed(2));

	}

	back_to_top ()
	{
		document.body.scrollTop = 0; // For Safari
		document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
	}

	async download_clicked(v, e) {
		// Get the table element
		let table = document.getElementById('attendance_report');
		
		let csv_string = '';
		
		if (this.selected_employee() && this.selected_employee().firstname)
			csv_string += `Employee: ${this.selected_employee().firstname} ${this.selected_employee().surname || ''}\n`;
		
		let start_date = new Date(this.date_start());
		let end_date = new Date(this.date_end());
		
		let formatted_start_date = moment(start_date).format('DD/MM/YYYY');
		let formatted_end_date = moment(end_date).format('DD/MM/YYYY');
		csv_string += `Period: ${formatted_start_date} to ${formatted_end_date}\n\n`;
	
		// Add table headers
		let thead = table.querySelector('thead');
		if (thead) {
			let header_row = thead.querySelector('tr');
			if (header_row) {
				let headers = [];
				let header_cells = header_row.querySelectorAll('th');
				header_cells.forEach(cell => {
					let text = cell.textContent.trim();
					// Escape commas and quotes in headers
					headers.push(text.includes(',') || text.includes('"') ? `"${text.replace(/"/g, '""')}"` : text);
				});
				csv_string += headers.join(',') + '\n';
			}
		}
	
		// Get CSV string from the tbody (which has the main data)
		let tbody = document.getElementById('time_attendance_report');
		if (!tbody) {
			return;
		}
	
		let tbody_rows = tbody.querySelectorAll('tr');
		tbody_rows.forEach(row => {
			let row_data = [];
			let cells = row.querySelectorAll('td');
			cells.forEach(cell => {
				let text = cell.textContent.trim();
				// For time cells that might contain comma-separated values, wrap in quotes
				if (cell.classList.contains('time-cell') || text.includes(',') || text.includes('"')) {
					// Escape existing quotes by doubling them, then wrap in quotes
					text = `"${text.replace(/"/g, '""')}"`;
				}
				row_data.push(text);
			});
			csv_string += row_data.join(',') + '\n';
		});
	
		// Add footer row
		let footer_row = table.querySelector('tfoot tr');
		if (footer_row) {
			// Initialize footer line with "Totals" and empty cells for first 3 columns
			let footer_line = ['Totals', '', ''];
			
			let total_cells = footer_row.querySelectorAll('td:not(:first-child)');
			// Add the totals
			total_cells.forEach(cell => {
				let text = cell.textContent.trim();
				footer_line.push(text.includes(',') || text.includes('"') ? `"${text.replace(/"/g, '""')}"` : text);
			});
			
			csv_string += footer_line.join(',') + '\n';
		}
	
		// Create and trigger download
		let blob = new Blob([csv_string], { type: 'text/csv;charset=utf-8;' });
		let link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.setAttribute('download', 'time_attendance_report.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
	
	show_report_help()
	{
		Grape.dialog.open('AttendanceEmployeeReportHelp', { employee_id: this.page.bindings.id });
	}
}

class ReportTimeAttendanceReportPerEmployeePage
{
	constructor(bindings)
	{
		this.bindings = bindings;
		this.viewModel = new ReportTimeAttendancePerEmployeeViewModel(this);
	}

	async init()
	{
		// check access to page
		if (await Utils.redirectOnAccessDenied('DepartmentsAvgLeaveComparisonReport'))
			return;

		document.title = tr('Attendance Report');
		window.current_page_title(tr('Attendance Report Per Employee'));
		this.updateData();
	}

	async updateData() 
	{
    	// Don't make API call if no employee is selected
    	if (!this.viewModel.current_employee_id())
		{
    	    this.viewModel.attendance_data([]);
    	    return;
    	}

    	// Detect client timezone
    	let client_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    	let params = {
    	    date_start: this.viewModel.date_start(),
    	    date_end: this.viewModel.date_end(),
    	    department_id: this.viewModel.current_department_id(),
    	    employee_id: this.viewModel.current_employee_id(),
    	    client_timezone: client_timezone
    	};

    	try {
    	    let res = await Grape.fetches.postJSON('/reports/time_and_attendance_report_employee', params);

    	    // Since your response is a direct array, process it directly
    	    if (Array.isArray(res) && res.length > 0) {
    	        this.process_results(res);
    	    } else {
    	        // Clear data if no results
    	        this.viewModel.attendance_data([]);
    	    }
		
    	} catch (error) {
    	    console.error('API Error:', error);
    	    // Don't show error popup - just clear data silently
    	    this.viewModel.attendance_data([]);
    	}
	}

	process_results(results)
	{
	    try {
	        let mapped_data = results.map((item) => {
	            let formatted_work_day = moment(item.work_day).format('DD/MM/YYYY');

	            // Handle empty start/end times - show dash instead of empty string
	            let formatted_start_time = item.task_start_time && item.task_start_time !== '' ? item.task_start_time : '-';
	            let formatted_end_time = item.task_end_time && item.task_end_time !== '' ? item.task_end_time : '-';
			
	            let total_hours = parseFloat(item.task_total_hours);
	            let formatted_total_hours = !isNaN(total_hours) ? total_hours.toFixed(2) : '0.00';

	            return {
	                work_day: formatted_work_day,
	                task_start_time: formatted_start_time,
	                task_end_time: formatted_end_time,
	                task_total_hours: formatted_total_hours,
	                leave_hours: item.leave_hours || '0.00',
	                holiday_hours: item.holiday_hours || '0.00',
	                employee_firstname: item.employee_firstname || '',
	                leave_type: item.leave_type || '',
	                holiday_description: item.holiday_description || ''
	            };
	        });

	        this.viewModel.attendance_data(mapped_data);
		
	    } catch (error) {
	        console.error('Error in process_results:', error);
	        // Clear data on error but don't show popup
	        this.viewModel.attendance_data([]);
	    }
	}
}

export default {
	route: '[/]time_management/report_time_attendance_per_employee',
	page_class: ReportTimeAttendanceReportPerEmployeePage,
	template: template,
	template_filename: 'translation/report_time_attendance_per_employee',
};